// Sidebar.js — Menu lateral da área "Minha Conta".
//
// ================= A ARRUMAÇÃO DE 11/09/2026 =================
//
// Eram nove itens iguais, um embaixo do outro, ocupando quase 500px de altura —
// mais do que o conteúdo de metade das abas. Três deles ("Dados Pessoais",
// "Endereços de Entrega", "Senha e Segurança") eram o mesmo assunto, e os dois
// que a editora mais quer que a pessoa abra — a Loja de Créditos e o Clube —
// tinham exatamente o mesmo peso visual de "Minha Lista".
//
// O que mudou, a pedido do dono:
//
//   1. ENDEREÇOS E SENHA VIRARAM SUB-ABAS de Dados Pessoais (ver
//      dadosPessoais/DadosPessoais.js). O menu perdeu duas linhas.
//   2. LOJA DE CRÉDITOS E CLUBE VIRARAM CARTÕES, cada um na cor do seu crédito
//      (laranja das Coins, vermelho do Ticket), com uma linha de apoio dizendo
//      o que é. São os dois destinos que geram receita; no meio de uma lista
//      cinza, eles simplesmente não eram vistos.
//   3. AS LINHAS ENCOLHERAM: `py-2.5` e ícone de 16px no lugar de `py-3` e
//      18px, e o rótulo "Geral" saiu — ele nomeava um grupo que não tinha par
//      até o fim da lista.
//
// A ORDEM É A PEDIDA, e não alfabética nem por frequência: Dados, E-books,
// Avaliações, Minha Lista, os dois destaques, e o Registro de Compras por
// último — é a única que se consulta de vez em quando, não toda visita.
import React from "react";
import { useNavigate } from "react-router-dom";
import SeletorDeSecao from "./SeletorDeSecao";

// `chaves` existe para o item continuar aceso quando a aba ativa é uma
// SUB-ABA dele: "enderecos" e "seguranca" ainda chegam por `changePerfiltab`
// de telas antigas (o checkout manda cadastrar endereço, por exemplo).
const GERAL = [
  { key: "dados", label: "Dados Pessoais", icon: "bi-person-circle", chaves: ["dados", "enderecos", "seguranca"] },
  // "E-books", e não "Meus E-books": a aba passou a listar também o que a
  // pessoa lê pelo Clube sem ter comprado, e o "Meus" prometia posse. A CHAVE
  // segue a mesma — telas antigas disparam `changePerfiltab` com ela.
  { key: "meus-livros", label: "E-books", icon: "bi-collection-play" },
  { key: "avaliacoes", label: "Avaliações", icon: "bi-star" },
  { key: "minha-lista", label: "Minha Lista", icon: "bi-heart" },
];

// Os dois cartões. `cor` é escrita por extenso nas classes abaixo porque o
// Tailwind só gera o que encontra literalmente no código.
const DESTAQUES = [
  {
    key: "creditos",
    label: "Loja de Créditos",
    nota: "Tickets e Coins",
    icon: "bi-bag-plus-fill",
    cor: "coin",
    chaves: ["creditos", "coins"],
  },
  {
    key: "clube",
    label: "Clube Insigne",
    nota: "Leia o acervo inteiro",
    icon: "bi-stars",
    cor: "ticket",
  },
];

// O azul claro da paleta (`insigne-navy-light`) neste item, a pedido do dono:
// ele fecha a coluna, vem depois dos dois cartões coloridos e é o único que
// não é "onde eu uso o site" — é onde eu confiro o que já fiz. A cor o separa
// dos quatro de cima sem lhe dar o peso de um destaque.
const FIM = [
  { key: "meus-pedidos", label: "Registro de Compras", icon: "bi-bag-check", cor: "azul" },
];

// UMA entrada, e não três.
//
// Eram "Dashboard do Autor", "Meus Livros" e "Vendas" — três telas mostrando
// recortes dos mesmos números, e nenhuma respondendo à pergunta que o autor
// realmente faz: "como está indo O MEU LIVRO?". Agora é lista de livros →
// clica → estatísticas e comentários daquele livro.
//
// As chaves antigas continuam funcionando no Perfil.js: telas legadas ainda
// disparam `changePerfiltab` com elas, e um evento que cai em lugar nenhum é
// um botão que não faz nada.
const AUTOR = [
  { key: "area-autor", label: "Área do Autor", icon: "bi-journal-bookmark" },
];

const TINTA = {
  coin: {
    parado: "border-insigne-orange/30 bg-insigne-orange/[0.07] hover:bg-insigne-orange/[0.13] hover:border-insigne-orange/50",
    ativo: "border-insigne-orange bg-insigne-orange/20",
    chip: "bg-insigne-orange/20 text-insigne-orange",
    texto: "text-insigne-orange",
  },
  ticket: {
    parado: "border-insigne-ticket/30 bg-insigne-ticket/[0.07] hover:bg-insigne-ticket/[0.13] hover:border-insigne-ticket/50",
    ativo: "border-insigne-ticket bg-insigne-ticket/20",
    chip: "bg-insigne-ticket/20 text-insigne-ticket",
    texto: "text-insigne-ticket",
  },
  // O Registro de Compras nao e um cartao de destaque, mas tem cor propria: o
  // azul claro da familia navy. ACESO ELE CONTINUA AZUL (pedido do dono,
  // 11/09/2026) -- pintar de laranja o unico item que se identifica pelo azul
  // fazia a cor parecer um estado, e nao o nome dele.
  azul: {
    parado: "border-insigne-navy-light/30 bg-insigne-navy-light/[0.07] hover:bg-insigne-navy-light/[0.13] hover:border-insigne-navy-light/50",
    ativo: "border-insigne-navy-light bg-insigne-navy-light",
    chip: "bg-insigne-navy-light/20 text-insigne-navy-light",
    texto: "text-insigne-navy-light",
  },
};

const Sidebar = ({ activeTab, onTabChange, isAuthor }) => {
  const navigate = useNavigate();
  const main = process.env.REACT_APP_MAIN;
  const go = (item) => (item.route ? navigate(main + item.route) : onTabChange(item.key));
  const estaAtivo = (item) =>
    !item.route && (item.chaves ? item.chaves.includes(activeTab) : activeTab === item.key);

  const Item = ({ item }) => (
    <button
      onClick={() => go(item)}
      aria-current={estaAtivo(item) ? "page" : undefined}
      className={
        // `rounded-controle` (6px) é o raio de BOTÃO do site — o mesmo dos
        // controles do resto da loja. Estava em `rounded-lg` (8px), e com os
        // itens colados a curva grande fazia a lista parecer uma pilha de
        // pastilhas. Pedido do dono em 11/09/2026: mais quadradinhos.
        "foco-laranja flex items-center gap-2.5 w-full text-left px-3 py-2.5 rounded-controle text-sm " +
        "font-medium transition-colors " +
        (estaAtivo(item)
          ? item.cor === "azul"
            ? "bg-insigne-navy-light text-paper shadow-[0_4px_16px_rgb(var(--c-navy-light)_/_0.35)]"
            : "bg-insigne-orange text-paper shadow-[0_4px_16px_rgba(250,127,8,0.3)]"
          : "text-white/75 hover:bg-white/5 hover:text-white")
      }
    >
      {/* Ícone e rótulo levam a MESMA cor: com só o texto em azul, o ícone
          ficava branco ao lado e o item parecia meio pintado. */}
      <i
        className={"bi " + item.icon + " text-base w-5 text-center " +
                   (!estaAtivo(item) && item.cor === "azul" ? "text-insigne-navy-light" : "")}
      />
      <span className={!estaAtivo(item) && item.cor === "azul" ? "text-insigne-navy-light" : ""}>
        {item.label}
      </span>
    </button>
  );

  /** Os dois destinos que a editora quer que sejam vistos. */
  const Destaque = ({ item }) => {
    const tinta = TINTA[item.cor];
    const ativo = estaAtivo(item);
    return (
      <button
        onClick={() => go(item)}
        aria-current={ativo ? "page" : undefined}
        className={
          "foco-laranja flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2.5 text-left " +
          "transition-colors " + (ativo ? tinta.ativo : tinta.parado)
        }
      >
        <span className={"inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-controle text-sm " + tinta.chip}>
          <i className={"bi " + item.icon} />
        </span>
        <span className="min-w-0">
          <span className={"block truncate text-sm font-bold leading-tight " + (ativo ? "text-white" : tinta.texto)}>
            {item.label}
          </span>
          <span className="block truncate text-[11px] leading-tight text-white/50">{item.nota}</span>
        </span>
      </button>
    );
  };

  const todos = [...GERAL, ...DESTAQUES, ...FIM, ...(isAuthor ? AUTOR : [])];

  // Os mesmos grupos do desktop, para a folha do celular (SeletorDeSecao).
  // Os títulos existem porque na folha as seções não têm a separação visual
  // que a coluna dá com os espaços entre os blocos.
  const grupos = [
    { titulo: "Geral", itens: GERAL },
    { titulo: "Créditos e assinatura", itens: DESTAQUES },
    { titulo: "Histórico", itens: FIM },
    ...(isAuthor ? [{ titulo: "Autor", itens: AUTOR }] : []),
  ];

  return (
    // 220px: o rótulo mais longo ("Registro de Compras", 14px) mede ~150px, e
    // com o ícone e o respiro lateral fecha em 210. Era 280 desde o começo, e
    // sobravam 70px de vazio em toda a coluna — o dono pediu duas vezes para
    // encolher. Cada pixel aqui vira largura de card na aba ao lado.
    <aside className="w-full md:w-[220px] shrink-0">
      {/* CELULAR: um seletor de uma linha, e não mais uma fileira de abas —
          ver o cabeçalho de SeletorDeSecao.js. Ele resolve o "duas barras de
          navegação empilhadas" que o dono apontou: a de baixo (as sub-abas da
          seção aberta) passa a ser a única fileira de pílulas da tela.
          `mb-4` dá o respiro até ela. */}
      <div className="mb-4">
        <SeletorDeSecao
          grupos={grupos}
          atual={todos.find(estaAtivo)}
          estaAtivo={estaAtivo}
          aoEscolher={go}
          TINTA={TINTA}
        />
      </div>

      {/* Desktop */}
      {/* O RESPIRO ENTRE OS BOTÕES (pedido do dono, 11/09/2026).
          Era `gap-0.5` (2px): com o item ativo pintado de laranja, ele
          encostava no vizinho e os dois liam como um bloco só. 8px separam sem
          esticar a coluna — a economia de altura veio das linhas mais baixas,
          não de colar um botão no outro. */}
      <div className="hidden md:flex md:flex-col md:gap-3">
        <nav className="flex flex-col gap-2">
          {GERAL.map((item) => <Item key={item.key} item={item} />)}
        </nav>

        <div className="flex flex-col gap-2">
          {DESTAQUES.map((item) => <Destaque key={item.key} item={item} />)}
        </div>

        <nav className="flex flex-col gap-2">
          {FIM.map((item) => <Item key={item.key} item={item} />)}
        </nav>

        {isAuthor && (
          <>
            <p className="mt-1 mb-0.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-white/35">
              Autor
            </p>
            <nav className="flex flex-col gap-2">
              {AUTOR.map((item) => <Item key={item.key} item={item} />)}
            </nav>
          </>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
